<template>
  <section
    class="w-full col-span-full transition-colors duration-250"
    :style="{ backgroundColor: 'var(--p-brand-navy-500)' }"
  >
    <ContentContainer class="py-0">
      <div class="py-2 xl:py-4 xl:mx-auto flex items-center relative gap-2">
        <div class="flex items-center py-0 gap-4 xl:py-2 mr-auto">
          <NuxtLink
            to="/"
            alt="MBZUAI Home"
            @click="
              () => {
                handleToggleMegamenu(null);
                clearError({ redirect: '/' });
              }
            "
          >
            <MBZUAILogo
              :is-home-page="true"
              class="max-w-[150px] md:max-w-[200px] md:me-2"
            />
          </NuxtLink>
        </div>

        <MBZCTAButton
          severity="secondary"
          aria-label="Toggle Global Site Search"
          title="Toggle Global Site Search"
          class="xl:hidden! rounded-[4px] py-0! px-0! w-[30px]! h-[30px]! mr-2 bg-mbzuai-sand-300 border-none! text-center"
          @click="handleToggleSearchPanel"
        >
          <template #icon>
            <template v-if="!isSearchPanelOpen">
              <MBZIcon
                class="button-icon min-w-[14px] min-h-[14px]"
                name="search"
              />
            </template>
            <template v-else>
              <MBZIcon
                class="button-icon min-w-[14px] min-h-[14px]"
                name="times"
              />
            </template>
          </template>
        </MBZCTAButton>

        <!-- Mobile Menu (trigger + layered drawer) -->
        <NavigationMobileMenu :main-menu="mainMenu" />

        <!-- Desktop menu -->
        <div class="hidden xl:flex">
          <ul
            class="list-none p-0 flex grow gap-4 md:gap-5 xl:items-center xl:justify-end select-none flex-col xl:flex-row"
          >
            <!-- Main Level 0 menu items with click to open mega menu -->
            <li v-for="menu in mainMenu" :key="menu" class="relative">
              <template v-if="menu?.level_2">
                <button
                  class="flex items-center gap-2 py-2 font-medium transition-colors duration-150 cursor-pointer link"
                  @click="handleToggleMegamenu(menu)"
                >
                  <span
                    :class="{
                      'underline underline-offset-8':
                        activeMenuItem?.title === menu.title,
                    }"
                  >
                    {{ menu.title }}
                  </span>
                  <ChevronIcon
                    :direction="
                      activeMenuItem?.title === menu.title ? 'up' : 'down'
                    "
                    class="w-[8px]! min-w-[8px] text-mbzuai-sand-200!"
                  />
                </button>
              </template>

              <template v-else>
                <NuxtLink
                  class="flex no-underline! hover:underline! underline-offset-8 p-2 xl:px-4 items-center font-medium transition-colors duration-150 cursor-pointer"
                  :to="menu.relative"
                  @click="handleToggleMegamenu(null)"
                >
                  {{ menu.title }}
                </NuxtLink>
              </template>
            </li>
          </ul>
        </div>
      </div>
    </ContentContainer>

    <TransitionGroup>
      <!-- MEGAMENU -->
      <div
        v-if="isMegamenuOpen"
        id="megamenuContent"
        class="absolute hidden xl:block w-full xl:absolute background-pattern-white z-999"
      >
        <ContentContainer
          class="grid xl:grid-cols-[300px_1fr_1fr] items-center"
          :class="
            activeMenuItem?.field_image?.length &&
            `min-h-[${activeMenuItem.field_image[0]!.height}px]!`
          "
        >
          <!-- 1st column with title, description and related links -->
          <div class="flex flex-col gap-6 col-span-1 py-11 pe-6 self-stretch">
            <MBZCTADefault
              v-if="activeMenuItem"
              :label="activeMenuItem.title"
              :url="{
                href: activeMenuItem.relative,
                title: activeMenuItem.title,
              }"
              class="megamenu-primary-link text-h4"
              @click="handleToggleMegamenu(null)"
            />

            <p
              v-if="activeMenuItem?.field_description?.length"
              class="text-body-small"
            >
              <DrupalMarkup
                :content="
                  activeMenuItem.field_description.map(
                    (description) => description.value
                  )
                "
              />
            </p>

            <!-- Related links -->
            <section
              v-if="activeMenuItem?.field_related_links?.length"
              class="border-t border-mbzuai-navy-500! py-6 w-full flex flex-col gap-6"
            >
              <p class="text-body-small-medium text-mbzuai-dark-navy-500!">
                {{ $t('Related Links') }}:
              </p>

              <ul class="flex flex-row flex-wrap gap-4">
                <li
                  v-for="link in activeMenuItem?.field_related_links || []"
                  :key="link.title"
                  class="link text-body-small"
                >
                  <ParagraphCallToActionDefault
                    button-style="default"
                    :button-label="link.title"
                    :button-url="{
                      title: link.title,
                      href: link.uri,
                      external: link.options.external,
                    }"
                  />
                </li>
              </ul>
            </section>
          </div>

          <div
            class="col-span-2 grid grid-cols-subgrid relative z-0 bg-mbzuai-sand-100 self-stretch overflow-x-clip"
          >
            <div
              class="col-span-1 col-start-1 py-10 gap-2 xl:gap-4 z-10 w-full bg-surface-50"
            >
              <template
                v-for="item in activeMenuItem?.level_2"
                :key="item.title"
              >
                <template v-if="item.level_3?.length">
                  <div
                    class="px-10 py-1.5 mb-2 xl:mb-0 transition-colors duration-300 ease-in-out hover:bg-mbzuai-sand-100 hover:cursor-pointer"
                    :class="{
                      'bg-mbzuai-sand-100':
                        activeLevel2Item?.[0] === item.title,
                    }"
                    @click="handleLevel2Selection(item)"
                  >
                    <ParagraphCallToActionDefault
                      button-style="default"
                      :button-label="item.title"
                      :button-url="undefined"
                      icon="chevronRight"
                    />
                  </div>
                </template>
                <template v-else>
                  <div
                    class="px-10 py-1.5 mb-2 xl:mb-0 transition-colors duration-300 ease-in-out hover:bg-mbzuai-sand-100"
                  >
                    <MBZCTADefault
                      :label="item.title"
                      :url="{
                        title: item.title,
                        href: item.relative,
                        external: item.external,
                      }"
                      icon="chevronRight"
                      @click="handleToggleMegamenu(null)"
                    />
                  </div>
                </template>
              </template>
            </div>

            <div
              v-if="activeLevel2Item"
              class="z-0 col-start-2 row-start-1 row-span-1 px-10 py-10 gap-2 flex flex-col xl:gap-0 bg-mbzuai-sand-100"
            >
              <MBZCTADefault
                v-for="(menuItem, index) in activeLevel2Item[1]"
                :key="index"
                :label="menuItem.title"
                :url="{
                  title: menuItem.title,
                  href: menuItem.relative,
                  external: menuItem.external,
                }"
                :icon="null"
                class="py-1.5"
                @click="handleToggleMegamenu(null)"
              />
            </div>
            <div
              class="z-10 col-start-2 row-start-1 row-span-1 transition-transform ease-in-out duration-500"
              :class="activeLevel2Item ? 'translate-x-full' : ''"
            >
              <ParagraphImageDefault
                v-if="activeMenuItem?.field_image?.length"
                class=""
                :image="{
                  mediaImageUrl: activeMenuItem.field_image[0]!.url,
                  mediaImageAlt: activeMenuItem.field_image[0]!.alt,
                  mediaImageWidth: activeMenuItem.field_image[0]!.width,
                  mediaImageHeight: activeMenuItem.field_image[0]!.height,
                }"
              />

              <ParagraphImageDefault
                v-else
                :image="{
                  mediaImageUrl: '/images/megamenu_placeholder.jpg',
                  mediaImageAlt: 'Alt text to be added dynamically',
                  mediaImageWidth: 366,
                  mediaImageHeight: 382,
                }"
              />
            </div>
          </div>
        </ContentContainer>
      </div>
      <!-- END MEGAMENU -->
    </TransitionGroup>

    <TransitionGroup>
      <div
        v-if="isSearchPanelOpen"
        class="background-pattern-mono-light min-h-[270px] absolute w-full z-999"
      >
        <ContentContainer class="overflow-visible">
          <ParagraphSearchInputFormDefault
            search-placeholder="Search The Site"
            custom-class="py-[42px] lg:py-21 max-w-2xl w-full mx-auto"
            target-path="/search"
          >
            <template #label>
              <label class="text-h4" for="search">
                {{ $t('Search The Site') }}
              </label>
            </template>
          </ParagraphSearchInputFormDefault>
        </ContentContainer>
      </div>
    </TransitionGroup>
  </section>
</template>

<script setup lang="ts">
const { fetchMenu } = useDrupalCe();

const mainMenu = await fetchMenu('main');
const { isSearchPanelOpen, handleToggleSearchPanel } = useDrupalSearch();
const {
  isMegamenuOpen,
  activeMenuItem,
  activeLevel2Item,
  handleToggleMegamenu,
  handleLevel2Selection,
} = useMenu();
</script>

<style>
/* we will explain what these classes do next! */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.megamenu-primary-link {
  padding-inline-end: 0;
}

.megamenu-primary-link .button-icon[data-position='right'] {
  position: static;
  display: inline-block;
  margin-inline-start: 8px;
  transform: none;
  vertical-align: middle;
}

html[lang='ar'] .megamenu-primary-link .button-icon[data-position='right'],
html[dir='rtl'] .megamenu-primary-link .button-icon[data-position='right'] {
  position: static;
  margin-inline-start: 8px;
  transform: rotate(180deg);
}

html[lang='ar'] .megamenu-primary-link,
html[dir='rtl'] .megamenu-primary-link {
  padding-left: 0;
}
</style>
